/**
 * Scroll depth tracking
 * Fires events at 25%, 50%, 75%, and 90% scroll thresholds
 * Each threshold only fires once per session
 */

import { pushEvent } from './dataLayer.js';
import { getPlatform } from './attribution.js';

const SCROLL_THRESHOLDS = [25, 50, 75, 90];
const STORAGE_KEY = 'azai_scroll_depths';
const THROTTLE_MS = 100;

// Track which thresholds have been fired this session
let firedThresholds = new Set();
let scrollListenerAttached = false;
let throttleTimer = null;

/**
 * Get scroll percentage
 * @returns {number} - Scroll percentage (0-100)
 */
const getScrollPercent = () => {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return 0;
    }
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    
    if (scrollHeight <= 0) {
      return 100; // Page is not scrollable
    }
    
    return Math.round((scrollTop / scrollHeight) * 100);
  } catch (error) {
    return 0;
  }
};

/**
 * Load previously fired thresholds from sessionStorage
 */
const loadFiredThresholds = () => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      firedThresholds = new Set(parsed);
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Save fired thresholds to sessionStorage
 */
const saveFiredThresholds = () => {
  try {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...firedThresholds]));
  } catch (error) {
    // Silent fail
  }
};

/**
 * Check and fire scroll depth events
 */
const checkScrollDepth = () => {
  try {
    const scrollPercent = getScrollPercent();
    
    for (const threshold of SCROLL_THRESHOLDS) {
      // Check if we've passed this threshold and haven't fired it yet
      if (scrollPercent >= threshold && !firedThresholds.has(threshold)) {
        firedThresholds.add(threshold);
        saveFiredThresholds();
        
        pushEvent('scroll_depth', {
          depth: threshold,
          platform: getPlatform(),
        });
      }
    }
    
    // If all thresholds fired, remove listener
    if (firedThresholds.size >= SCROLL_THRESHOLDS.length) {
      removeScrollListener();
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Throttled scroll handler
 */
const handleScroll = () => {
  if (throttleTimer) {
    return;
  }
  
  throttleTimer = setTimeout(() => {
    throttleTimer = null;
    checkScrollDepth();
  }, THROTTLE_MS);
};

/**
 * Remove scroll listener
 */
const removeScrollListener = () => {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    
    window.removeEventListener('scroll', handleScroll);
    scrollListenerAttached = false;
    
    if (throttleTimer) {
      clearTimeout(throttleTimer);
      throttleTimer = null;
    }
  } catch (error) {
    // Silent fail
  }
};

/**
 * Initialize scroll depth tracking
 * @returns {Function} - Cleanup function to remove listener
 */
export const initScrollTracking = () => {
  try {
    if (typeof window === 'undefined') {
      return () => {};
    }
    
    // Don't attach if already attached
    if (scrollListenerAttached) {
      return removeScrollListener;
    }
    
    // Load any previously fired thresholds
    loadFiredThresholds();
    
    // If all thresholds already fired, don't attach listener
    if (firedThresholds.size >= SCROLL_THRESHOLDS.length) {
      return () => {};
    }
    
    // Attach scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    scrollListenerAttached = true;
    
    // Check initial scroll position (user might have scrolled before JS loaded)
    checkScrollDepth();
    
    return removeScrollListener;
  } catch (error) {
    return () => {};
  }
};

/**
 * Reset scroll tracking (useful for testing)
 */
export const resetScrollTracking = () => {
  try {
    firedThresholds.clear();
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
    removeScrollListener();
  } catch (error) {
    // Silent fail
  }
};

export default {
  initScrollTracking,
  resetScrollTracking,
};
